import PrescriptionModelReference, { Identifier } from './PrescriptionModelReference';
import { MultiLayerSeedingPrescriptionStrategyV1 } from '../../components/Prescription/SeedingJob/SeedingJobStrategies/MultiLayerSeedingPrescriptionStrategyV1';
import { AnyPrescriptionJobStrategy } from '../../components/Prescription/PrescriptionJobStrategy';

/**
 * Registry and factory for versioned prescription-model strategies.
 *
 * A {@link PrescriptionModelReference} is persisted with a prescription job so that reopening the
 * job uses the same calculation model that originally created it. Consequently, archived jobs must
 * be resolved with {@link getPrescriptionStrategyFromRef}; they must not be silently upgraded to the
 * latest strategy. {@link getLatestPrescriptionModelStrategy} is intended for new jobs only.
 *
 * OBS! A released model version is immutable. Its implementation must not be changed, because doing so
 * would alter the results produced when existing jobs are reopened. Any change to model behavior
 * must instead be implemented and registered as a new model version.
 *
 * Adding a model version requires all the following:
 * - Implement a new {@link PrescriptionJobStrategy}.
 * - Add its version to {@link VersionsExistingForModels}.
 * - Add the matching identifier/version branch to {@link getPrescriptionStrategyFromRef}.
 * - Make the strategy's `toReference()` return that same identifier and version.
 */

/**
 * Available strategy versions grouped by their stable persisted identifier.
 *
 * Each identifier must have at least one version. Version numbers do not need to be sorted because
 * the latest version is selected numerically with `Math.max`.
 */
const VersionsExistingForModels = {
  [Identifier.MultiLayerSeedingModel]: [1],
};

/**
 * Creates the exact strategy described by a persisted model reference.
 *
 * Resolution is deliberately strict: an unknown identifier or unsupported version throws instead
 * of falling back to another model. This preserves the calculation behavior of archived jobs and
 * makes missing migrations or registrations visible.
 *
 * A fresh strategy instance is returned for every call.
 *
 * @param prescriptionModelReference Stable model identifier and version stored with a job.
 * @returns The strategy implementation registered for the referenced model version.
 * @throws If the identifier is unknown or its version is not registered.
 */
export const getPrescriptionStrategyFromRef = (
  prescriptionModelReference: PrescriptionModelReference
): AnyPrescriptionJobStrategy => {
  switch (prescriptionModelReference.identifier) {
    case Identifier.MultiLayerSeedingModel:
      switch (prescriptionModelReference.version) {
        case 1:
          return new MultiLayerSeedingPrescriptionStrategyV1();

        default:
          throw new Error(
            `Could not determine prescription model for identifier ${prescriptionModelReference.identifier} , v${prescriptionModelReference.version}`
          );
      }

    default:
      throw new Error(
        `Could not determine prescription model for identifier ${prescriptionModelReference.identifier}`
      );
  }
};

/**
 * Finds the greatest registered version number for a prescription-model identifier.
 *
 * @param identifier Stable identifier of the prescription model.
 * @returns The newest registered numeric version.
 */
const getNewestVersionForIdentifier = (identifier: Identifier): number => {
  const availableVersions: number[] = VersionsExistingForModels[identifier];
  return Math.max(...availableVersions);
};

/**
 * Creates the newest registered strategy for a prescription model.
 *
 * Use this when starting a new prescription job. To reopen an existing job, use
 * {@link getPrescriptionStrategyFromRef} so that the persisted version remains authoritative.
 *
 * @param identifier Stable identifier of the desired prescription model.
 * @returns A new instance of the strategy with the greatest registered version number.
 * @throws If the identifier has no registered strategy or its newest version is not handled by the
 * strategy factory.
 */
export const getLatestPrescriptionModelStrategy = (
  identifier: Identifier
): AnyPrescriptionJobStrategy => {
  const newestVersionForModel: number = getNewestVersionForIdentifier(identifier);
  return getPrescriptionStrategyFromRef(
    new PrescriptionModelReference(identifier, newestVersionForModel)
  );
};
